// Authored question lists for the party-game pages. Original writing, family
// friendly by default; no scraping. Categories keep the generator filterable.

export type PartyCategory =
  | 'funny'
  | 'deep'
  | 'food'
  | 'travel'
  | 'daily'
  | 'truth'
  | 'dare'
  | 'work'
  | 'geography'
  | 'science'
  | 'history'
  | 'culture';

export type PartyQuestion = {
  text: string;
  category: PartyCategory;
  /** Present on trivia only: the answer revealed after the guess. */
  answer?: string;
};

export const WOULD_YOU_RATHER: PartyQuestion[] = [
  {
    text: 'Would you rather be able to fly, but only at walking speed, or teleport, but only once a day?',
    category: 'funny',
  },
  {
    text: 'Would you rather always have to sing instead of speak, or dance everywhere you walk?',
    category: 'funny',
  },
  {
    text: 'Would you rather fight one horse-sized duck or a hundred duck-sized horses?',
    category: 'funny',
  },
  { text: 'Would you rather have hands for feet or feet for hands?', category: 'funny' },
  { text: 'Would you rather sneeze glitter or cry maple syrup?', category: 'funny' },
  {
    text: 'Would you rather your phone always be at 5 percent or your car always be nearly out of fuel?',
    category: 'funny',
  },
  {
    text: 'Would you rather speak every language badly or one extra language perfectly?',
    category: 'funny',
  },
  {
    text: 'Would you rather be famous for something embarrassing or unknown for something great?',
    category: 'deep',
  },
  { text: 'Would you rather know how you die or when you die?', category: 'deep' },
  {
    text: 'Would you rather relive one perfect day forever or live a normal life you cannot repeat?',
    category: 'deep',
  },
  {
    text: 'Would you rather be the smartest person in the room or the funniest?',
    category: 'deep',
  },
  { text: 'Would you rather lose all your memories or never make new ones?', category: 'deep' },
  { text: 'Would you rather always say what you think or never speak again?', category: 'deep' },
  {
    text: 'Would you rather have true love once or comfortable friendship forever?',
    category: 'deep',
  },
  {
    text: 'Would you rather restart your life at age ten with everything you know, or jump ten years ahead?',
    category: 'deep',
  },
  { text: 'Would you rather give up cheese or chocolate forever?', category: 'food' },
  {
    text: 'Would you rather eat only breakfast food or only dinner food for a year?',
    category: 'food',
  },
  { text: 'Would you rather have unlimited free pizza or unlimited free sushi?', category: 'food' },
  {
    text: 'Would you rather never taste anything spicy again or never taste anything sweet?',
    category: 'food',
  },
  {
    text: 'Would you rather drink only water forever or never drink water directly again?',
    category: 'food',
  },
  {
    text: 'Would you rather eat your favorite meal every day or never eat it again?',
    category: 'food',
  },
  {
    text: 'Would you rather always cook for others or always have others cook for you?',
    category: 'food',
  },
  {
    text: 'Would you rather live out of a suitcase forever or never leave your hometown again?',
    category: 'travel',
  },
  {
    text: 'Would you rather see the past of every place you visit or its future?',
    category: 'travel',
  },
  {
    text: 'Would you rather have a free flight anywhere every month or free hotels everywhere forever?',
    category: 'travel',
  },
  { text: 'Would you rather explore the deep ocean or outer space?', category: 'travel' },
  { text: 'Would you rather vacation only in cities or only in nature?', category: 'travel' },
  {
    text: 'Would you rather move to a new country every year or never move again?',
    category: 'travel',
  },
  {
    text: 'Would you rather always be ten minutes late or always forty minutes early?',
    category: 'daily',
  },
  {
    text: 'Would you rather never wait in line again or never sit in traffic again?',
    category: 'daily',
  },
  {
    text: 'Would you rather have a rewind button or a pause button for your life?',
    category: 'daily',
  },
  {
    text: 'Would you rather work your dream job for average pay or a boring job for triple pay?',
    category: 'daily',
  },
  {
    text: 'Would you rather always have perfect weather or always have perfect wifi?',
    category: 'daily',
  },
  { text: 'Would you rather never do laundry again or never do dishes again?', category: 'daily' },
  {
    text: 'Would you rather sleep four hours and feel rested or need ten hours but dream anything you choose?',
    category: 'daily',
  },
  {
    text: 'Would you rather your browser history be public or your bank balance?',
    category: 'funny',
  },
  { text: 'Would you rather high-five everyone you meet or bow to them?', category: 'funny' },
  {
    text: 'Would you rather have a personal theme song that plays when you enter a room or a laugh track that follows your jokes?',
    category: 'funny',
  },
  {
    text: 'Would you rather be able to talk to animals or read human minds once a day?',
    category: 'deep',
  },
  { text: 'Would you rather be respected or liked?', category: 'deep' },
];

export const NEVER_HAVE_I_EVER: PartyQuestion[] = [
  {
    text: 'Never have I ever pretended to know a song and mouthed the wrong words.',
    category: 'funny',
  },
  {
    text: 'Never have I ever waved back at someone who was waving at the person behind me.',
    category: 'funny',
  },
  { text: 'Never have I ever walked into a glass door.', category: 'funny' },
  {
    text: 'Never have I ever sent a message to the person the message was about.',
    category: 'funny',
  },
  { text: 'Never have I ever laughed at a joke I did not understand.', category: 'funny' },
  { text: 'Never have I ever practiced a conversation in the mirror.', category: 'funny' },
  { text: 'Never have I ever pretended my phone died to leave a conversation.', category: 'funny' },
  {
    text: 'Never have I ever worn something inside out all day without noticing.',
    category: 'funny',
  },
  { text: 'Never have I ever forgotten someone’s name while introducing them.', category: 'funny' },
  { text: 'Never have I ever cried at a commercial.', category: 'deep' },
  { text: 'Never have I ever kept a gift I said I loved but never used.', category: 'deep' },
  { text: 'Never have I ever googled myself.', category: 'deep' },
  { text: 'Never have I ever stayed friends with someone only out of habit.', category: 'deep' },
  { text: 'Never have I ever changed my opinion just to end an argument.', category: 'deep' },
  { text: 'Never have I ever regretted something I posted online.', category: 'deep' },
  {
    text: 'Never have I ever eaten dessert before dinner and skipped the dinner.',
    category: 'food',
  },
  { text: 'Never have I ever eaten food that fell on the floor.', category: 'food' },
  {
    text: 'Never have I ever ordered the same dish at a restaurant every single time.',
    category: 'food',
  },
  { text: 'Never have I ever finished a whole tub of ice cream in one sitting.', category: 'food' },
  { text: 'Never have I ever pretended to like a meal someone cooked for me.', category: 'food' },
  { text: 'Never have I ever burned water, somehow.', category: 'food' },
  { text: 'Never have I ever missed a flight.', category: 'travel' },
  {
    text: 'Never have I ever slept through an entire flight, takeoff to landing.',
    category: 'travel',
  },
  { text: 'Never have I ever gotten lost in the city I grew up in.', category: 'travel' },
  { text: 'Never have I ever packed a suitcase an hour before leaving.', category: 'travel' },
  {
    text: 'Never have I ever taken a photo of food before anyone could touch it.',
    category: 'travel',
  },
  {
    text: 'Never have I ever said "I know where we are" while completely lost.',
    category: 'travel',
  },
  { text: 'Never have I ever hit snooze more than three times in one morning.', category: 'daily' },
  { text: 'Never have I ever talked to myself out loud in public.', category: 'daily' },
  {
    text: 'Never have I ever rewatched a series instead of starting a new one.',
    category: 'daily',
  },
  {
    text: 'Never have I ever put off replying to a message for more than a week.',
    category: 'daily',
  },
  { text: 'Never have I ever fallen asleep during a movie I chose.', category: 'daily' },
  {
    text: 'Never have I ever worn pajamas on a video call below the desk line.',
    category: 'daily',
  },
  { text: 'Never have I ever clapped when the plane landed.', category: 'travel' },
  { text: 'Never have I ever lost a staring contest with a pet.', category: 'funny' },
  {
    text: 'Never have I ever said "let’s do this again soon" without meaning it.',
    category: 'deep',
  },
];

export const TRUTH_OR_DARE: PartyQuestion[] = [
  { text: 'What is the last thing you searched for on your phone?', category: 'truth' },
  { text: 'What is a small lie you tell all the time?', category: 'truth' },
  { text: 'Which app do you open first every morning, honestly?', category: 'truth' },
  { text: 'What is the most embarrassing song on your playlist?', category: 'truth' },
  { text: 'Who in this room would you call first in an emergency?', category: 'truth' },
  { text: 'What is a compliment you have never said out loud to someone here?', category: 'truth' },
  { text: 'What is the worst haircut you have ever had?', category: 'truth' },
  { text: 'What is something you pretend to enjoy but really do not?', category: 'truth' },
  { text: 'What is the longest you have gone without sleeping?', category: 'truth' },
  { text: 'What is a talent you have that nobody here knows about?', category: 'truth' },
  { text: 'What is the worst gift you have ever received?', category: 'truth' },
  { text: 'What rule do you break most often?', category: 'truth' },
  { text: 'What is the most childish thing you still do?', category: 'truth' },
  { text: 'What is your most irrational fear?', category: 'truth' },
  { text: 'What is a moment you are genuinely proud of?', category: 'truth' },
  { text: 'Which fictional character do you relate to most, and why?', category: 'truth' },
  { text: 'Do your best impression of someone in this room.', category: 'dare' },
  { text: 'Speak in a made-up accent until your next turn.', category: 'dare' },
  { text: 'Let the person on your left choose your next drink or snack.', category: 'dare' },
  { text: 'Sing the chorus of the last song you listened to.', category: 'dare' },
  {
    text: 'Text a friend outside the room a single emoji and read their reply out loud.',
    category: 'dare',
  },
  { text: 'Balance a spoon on your nose for ten seconds.', category: 'dare' },
  { text: 'Do twenty jumping jacks while naming animals.', category: 'dare' },
  { text: 'Talk about your day as if you are narrating a nature documentary.', category: 'dare' },
  { text: 'Let someone else style your hair with whatever is nearby.', category: 'dare' },
  { text: 'Try to lick your elbow, and let everyone watch you try.', category: 'dare' },
  { text: 'Say the alphabet backwards, out loud, without help.', category: 'dare' },
  { text: 'Give a two-minute lecture on a topic you know nothing about.', category: 'dare' },
  { text: 'Swap one item of clothing with the person on your right.', category: 'dare' },
  { text: 'Do your best runway walk across the room and back.', category: 'dare' },
  { text: 'Hold a plank until your next turn comes around.', category: 'dare' },
  { text: 'Let the group pick one photo on your phone for you to describe.', category: 'dare' },
];

export const ICEBREAKERS: PartyQuestion[] = [
  { text: 'What is something you learned recently that surprised you?', category: 'work' },
  { text: 'What did you want to be when you were ten years old?', category: 'work' },
  { text: 'What is the best piece of advice you have been given at work?', category: 'work' },
  { text: 'If you could master one skill overnight, what would it be?', category: 'work' },
  { text: 'What is your go-to way to reset after a hard day?', category: 'daily' },
  { text: 'What is a small thing that instantly improves your morning?', category: 'daily' },
  { text: 'What is the best meal you have had in the last month?', category: 'food' },
  { text: 'Coffee, tea, or neither, and how do you take it?', category: 'food' },
  { text: 'What is a place you would go back to tomorrow if you could?', category: 'travel' },
  { text: 'Window seat or aisle seat, and how strongly do you feel?', category: 'travel' },
  { text: 'What is a hobby you have picked up in the last few years?', category: 'daily' },
  { text: 'What is the last thing that made you laugh out loud?', category: 'funny' },
  { text: 'If your week had a soundtrack, what song would open it?', category: 'funny' },
  { text: 'What is something you are looking forward to this month?', category: 'daily' },
  { text: 'What is a book, show, or podcast you would recommend to anyone?', category: 'culture' },
  { text: 'What is the most useful app on your phone right now?', category: 'work' },
  { text: 'What is one thing on your desk that has a story behind it?', category: 'work' },
  { text: 'What is a tradition from your family that you still keep?', category: 'culture' },
  { text: 'What is a skill from a past job that still helps you today?', category: 'work' },
  { text: 'If the team had an extra hour every Friday, how should we spend it?', category: 'work' },
  { text: 'What is your favorite way to spend a completely free Saturday?', category: 'daily' },
  { text: 'What is something you have changed your mind about recently?', category: 'deep' },
  { text: 'Who taught you something that stuck with you?', category: 'deep' },
  { text: 'What is a small win from this week worth sharing?', category: 'work' },
];

export const TRIVIA_QUESTIONS: PartyQuestion[] = [
  {
    text: 'What is the largest ocean on Earth?',
    answer: 'The Pacific Ocean',
    category: 'geography',
  },
  {
    text: 'Which country has the most time zones?',
    answer: 'France, counting its overseas territories',
    category: 'geography',
  },
  {
    text: 'What is the capital of Australia?',
    answer: 'Canberra, not Sydney',
    category: 'geography',
  },
  {
    text: 'Which desert is the largest in the world?',
    answer: 'The Antarctic Desert, a cold desert',
    category: 'geography',
  },
  {
    text: 'What is the longest river in South America?',
    answer: 'The Amazon',
    category: 'geography',
  },
  {
    text: 'Which two countries share the longest land border?',
    answer: 'Canada and the United States',
    category: 'geography',
  },
  {
    text: 'What is the smallest country in the world by area?',
    answer: 'Vatican City',
    category: 'geography',
  },
  { text: 'How many bones are in the adult human body?', answer: '206', category: 'science' },
  {
    text: 'What gas do plants absorb from the air for photosynthesis?',
    answer: 'Carbon dioxide',
    category: 'science',
  },
  { text: 'What is the chemical symbol for gold?', answer: 'Au', category: 'science' },
  {
    text: 'Which planet has the most moons discovered so far?',
    answer: 'Saturn',
    category: 'science',
  },
  {
    text: 'What is the hardest naturally occurring substance?',
    answer: 'Diamond',
    category: 'science',
  },
  {
    text: 'How long does light from the Sun take to reach Earth?',
    answer: 'About eight minutes and twenty seconds',
    category: 'science',
  },
  {
    text: 'What blood type is the universal donor for red cells?',
    answer: 'O negative',
    category: 'science',
  },
  {
    text: 'What is the freezing point of water in Fahrenheit?',
    answer: '32 degrees',
    category: 'science',
  },
  { text: 'In which year did the Berlin Wall fall?', answer: '1989', category: 'history' },
  {
    text: 'Who was the first person to walk on the Moon?',
    answer: 'Neil Armstrong',
    category: 'history',
  },
  {
    text: 'Which ancient civilization built Machu Picchu?',
    answer: 'The Inca',
    category: 'history',
  },
  { text: 'In what year did the Titanic sink?', answer: '1912', category: 'history' },
  {
    text: 'Which empire was ruled by Genghis Khan?',
    answer: 'The Mongol Empire',
    category: 'history',
  },
  {
    text: 'What was the first successful powered airplane flight year?',
    answer: '1903, by the Wright brothers',
    category: 'history',
  },
  {
    text: 'Which country gifted the Statue of Liberty to the United States?',
    answer: 'France',
    category: 'history',
  },
  { text: 'How many strings does a standard violin have?', answer: 'Four', category: 'culture' },
  {
    text: 'Which artist painted the ceiling of the Sistine Chapel?',
    answer: 'Michelangelo',
    category: 'culture',
  },
  {
    text: 'What language has the most native speakers worldwide?',
    answer: 'Mandarin Chinese',
    category: 'culture',
  },
  {
    text: 'How many players are on the field per team in soccer?',
    answer: 'Eleven',
    category: 'culture',
  },
  {
    text: 'What is the most widely used writing system in the world?',
    answer: 'The Latin alphabet',
    category: 'culture',
  },
  {
    text: 'Which board game features the properties Boardwalk and Park Place?',
    answer: 'Monopoly',
    category: 'culture',
  },
  {
    text: 'How many keys does a standard full-size piano have?',
    answer: '88',
    category: 'culture',
  },
  {
    text: 'What is the only mammal capable of true sustained flight?',
    answer: 'The bat',
    category: 'science',
  },
  { text: 'Which fruit has its seeds on the outside?', answer: 'The strawberry', category: 'food' },
  { text: 'What spice is derived from the crocus flower?', answer: 'Saffron', category: 'food' },
  {
    text: 'What are the two main ingredients of traditional pesto besides basil?',
    answer: 'Pine nuts and Parmesan cheese',
    category: 'food',
  },
  {
    text: 'Which country consumes the most coffee per person?',
    answer: 'Finland',
    category: 'food',
  },
  {
    text: 'What does the cooking term "al dente" mean?',
    answer: 'Firm to the bite',
    category: 'food',
  },
  {
    text: 'What is the tallest mountain above sea level?',
    answer: 'Mount Everest',
    category: 'geography',
  },
];
